/**
 * Cryptograms: a quotation enciphered with a random substitution alphabet.
 *
 * The cipher alphabet is a shuffle of A–Z in which no letter stands for itself (the rule
 * newspaper cryptograms follow), made by the cipher family's `randomKey` from the seeded
 * generator, so a puzzle is a pure function of (text, seed, options). Only the letters
 * A–Z are enciphered; accented Latin letters are folded to their base letter first.
 * Spaces and punctuation stay where they are — in the "aristocrat" form — unless the
 * text is re-set in five-letter groups with the punctuation removed — the harder
 * "patristocrat" of the American Cryptogram Association.
 */

import {
  ALPHABET,
  foldDiacritics,
  foreignLetters,
  letterIndex,
} from "@/lib/tool-kit/ciphers/alphabet";
import {
  randomKey,
  substitutionEncode,
} from "@/lib/tool-kit/ciphers/substitution";
import { createRng, randInt, shuffled } from "@/lib/tool-kit/worksheets/random";

export type WordBreaks = "words" | "groups";
export type GivenChoice = "random" | "commonest";

export const MAX_GIVEN = 6;
export const GROUP_SIZE = 5;
export const MIN_LETTERS = 12;
export const MAX_LETTERS = 400;

export type Token =
  | { kind: "letter"; cipher: string; plain: string; given: boolean }
  | { kind: "mark"; char: string };

export interface CryptogramOptions {
  text: string;
  seed: number;
  breaks: WordBreaks;
  /** How many letters to reveal. */
  given: number;
  givenChoice: GivenChoice;
}

export interface Cryptogram {
  /** The text in capitals, accents folded. */
  plain: string;
  /** The cipher alphabet: plain A–Z become these letters. */
  key: string;
  /** The words as laid out on the sheet, each a run of letters and marks. */
  words: Token[][];
  /** The plain letters revealed, in alphabetical order. */
  givenLetters: string[];
  /** How often each cipher letter occurs, A–Z. */
  counts: number[];
  letters: number;
  distinct: number;
}

export type TextProblem = "empty" | "short" | "long" | "foreign";

const WHITESPACE = /\s+/;

/** Whether a text can be a cryptogram, and what is wrong if it cannot. */
export function checkText(text: string): {
  problem: TextProblem | null;
  letters: number;
  foreign: string[];
} {
  const folded = foldDiacritics(text);
  const letters = [...folded].filter((char) => letterIndex(char) >= 0).length;
  const foreign = foreignLetters(text).letters;
  let problem: TextProblem | null = null;
  if (text.trim() === "") {
    problem = "empty";
  } else if (foreign.length > 0) {
    problem = "foreign";
  } else if (letters < MIN_LETTERS) {
    problem = "short";
  } else if (letters > MAX_LETTERS) {
    problem = "long";
  }
  return { problem, letters, foreign };
}

function letterCounts(plain: string): Map<string, number> {
  const counts = new Map<string, number>();
  for (const char of plain) {
    if (letterIndex(char) >= 0) {
      counts.set(char, (counts.get(char) ?? 0) + 1);
    }
  }
  return counts;
}

/** The plain letters to reveal: the commonest, or a seeded random few. */
export function chooseGiven(
  plain: string,
  count: number,
  choice: GivenChoice,
  rng: () => number
): string[] {
  const counts = letterCounts(plain);
  const letters = [...counts.keys()].sort();
  const wanted = Math.max(0, Math.min(MAX_GIVEN, count, letters.length - 1));
  const ordered =
    choice === "commonest"
      ? letters.sort(
          (a, b) =>
            (counts.get(b) ?? 0) - (counts.get(a) ?? 0) || a.localeCompare(b)
        )
      : shuffled(rng, letters);
  return ordered.slice(0, wanted).sort();
}

function layOut(plain: string, breaks: WordBreaks): string[][] {
  if (breaks === "words") {
    return plain
      .split(WHITESPACE)
      .filter((word) => word.length > 0)
      .map((word) => [...word]);
  }
  const letters = [...plain].filter((char) => letterIndex(char) >= 0);
  const groups: string[][] = [];
  for (let i = 0; i < letters.length; i += GROUP_SIZE) {
    groups.push(letters.slice(i, i + GROUP_SIZE));
  }
  return groups;
}

/** Build a cryptogram, or null when the text has too few letters or any outside A–Z. */
export function buildCryptogram(options: CryptogramOptions): Cryptogram | null {
  const check = checkText(options.text);
  if (check.problem !== null) {
    return null;
  }
  const plain = foldDiacritics(options.text).toUpperCase();
  const rng = createRng(options.seed);
  const key = randomKey((bound) => randInt(rng, 0, bound - 1));
  const given = chooseGiven(plain, options.given, options.givenChoice, rng);
  const words: Token[][] = layOut(plain, options.breaks).map((chars) =>
    chars.map((char): Token => {
      if (letterIndex(char) < 0) {
        return { kind: "mark", char };
      }
      return {
        kind: "letter",
        cipher: substitutionEncode(char, key),
        plain: char,
        given: given.includes(char),
      };
    })
  );
  const counts = new Array<number>(ALPHABET.length).fill(0);
  let letters = 0;
  for (const token of words.flat()) {
    if (token.kind === "letter") {
      counts[letterIndex(token.cipher)] += 1;
      letters += 1;
    }
  }
  return {
    plain,
    key,
    words,
    givenLetters: given,
    counts,
    letters,
    distinct: counts.filter((n) => n > 0).length,
  };
}

/** The cipher letter a plain letter becomes. */
export function cipherLetterFor(key: string, plain: string): string {
  return key.charAt(letterIndex(plain));
}

/** The text of the whole puzzle, for copying: the cipher text with its spacing. */
export function cipherText(puzzle: Cryptogram): string {
  return puzzle.words
    .map((word) =>
      word
        .map((token) => (token.kind === "letter" ? token.cipher : token.char))
        .join("")
    )
    .join(" ");
}
